import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {API_BASE_URL} from '../../configs/AppConfig';
import service from "../../auth/FetchInterceptor";
import {SORT_ORDER} from "../../constants/Constants";

// Async thunk to fetch links
export const fetchLinks = createAsyncThunk(
    'links/fetchLinks',
    async ({page = 1, pageSize = 20, sorter={}, filters, t}, {getState, rejectWithValue}) => {
        try {
            const queryParams = new URLSearchParams({
                page: page - 1,
                size: pageSize,
                ...getState().links.filters
            });
            // Handle sorting if sorter is provided
            if (sorter.field && sorter.order) {
                // Columns sorter sets order as 'ascend' but we send as 'asc'
                const sortOrder = sorter.order === SORT_ORDER.ASCEND ? SORT_ORDER.ASC : SORT_ORDER.DESC;
                queryParams.append(SORT_ORDER.SORT, `${sorter.field},${sortOrder}`);
            }
             // API call
            return await service.get(`${API_BASE_URL}/api/links?${queryParams.toString()}`); // Return the fetched data
        } catch (error) {
            return rejectWithValue(error.response?.data || t('error.failed.to.load.links'));
        }
    }
);
export const fetchTags = createAsyncThunk(
    'links/fetchTags',
    async ({t}, {getState, rejectWithValue}) => {
        try {
             // API call
            return await service.get(`${API_BASE_URL}/api/data/tags`); // Return the fetched data
        } catch (error) {
            return rejectWithValue(error.response?.data || t('error.failed.to.load.links'));
        }
    }
);

// Async thunk to fetch links by filter
export const fetchLinksByFilter = createAsyncThunk(
    'links/fetchLinksFiltered',
    async ({page = 1, pageSize = 20, sorter={}, filters, t}, { rejectWithValue }) => {
        try {
            // Convert filters object into query params
            const queryParams = new URLSearchParams({
                page: page - 1, // Backend uses 0-based index
                size: pageSize,
                ...filters
            });
            // Handle sorting if sorter is provided
            if (sorter.field && sorter.order) {
                // Columns sorter sets order as 'ascend' but we send as 'asc'
                const sortOrder = sorter.order === SORT_ORDER.ASCEND ? SORT_ORDER.ASC : SORT_ORDER.DESC;
                queryParams.append(SORT_ORDER.SORT, `${sorter.field},${sortOrder}`);
            }
            // API call
            return await service.get(`${API_BASE_URL}/api/links-filtered?${queryParams.toString()}`); // Return the fetched data
        } catch (error) {
            return rejectWithValue(error.response?.data || t('error.failed.to.search.links'));
        }
    }
);

// Add a new link
export const addLink = createAsyncThunk(
    "links/addLink",
    async ({newLink, t}, {dispatch, getState, rejectWithValue}) => {
        try {
            const response = await service.post(`${API_BASE_URL}/api/links`, newLink);
            dispatch(fetchLinks({ page: 1, pageSize: getState().links.pagination.pageSize, t:t }));
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || t("error.adding.link"));
        }
    }
);

// Update a link
export const updateLink = createAsyncThunk(
    "links/updateLink",
    async ({id, updatedData,t}, {dispatch, getState, rejectWithValue}) => {
        try {
            const response = await service.put(`${API_BASE_URL}/api/links/${id}`, updatedData);
            dispatch(fetchLinks({ page: getState().links.pagination.current, pageSize: getState().links.pagination.pageSize, t:t }));
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || t("error.updating.link"));
        }
    }
);

// Delete a link
export const deleteLink = createAsyncThunk(
    "links/deleteLink",
    async ({id, t}, {dispatch, getState, rejectWithValue}) => {
        try {
            await service.delete(`${API_BASE_URL}/api/links/${id}`);

            // Get current pagination state
            const { pagination, data } = getState().links;

            // Calculate the new total count
            const newTotal = pagination.total - 1;

            // If deleting the last item on the last page, move to previous page
            const newPage =
                pagination.current > 1 && data.length === 1
                    ? pagination.current - 1
                    : pagination.current;

            dispatch(fetchLinks({ page: newPage, pageSize: pagination.pageSize, t:t }));
            return id; // Return ID so we can remove it from state
        } catch (error) {
            return rejectWithValue(error.response?.data || t("error.deleting.link"));
        }
    }
);

// Async thunk to fetch links visibility
export const fetchLinksVisibility = createAsyncThunk(
    "links/visibility",
    async ({t}, {dispatch, getState, rejectWithValue}) => {
        try {
            return await service.get(`${API_BASE_URL}/api/links/visibility`);
        } catch (error) {
            return rejectWithValue(error.response?.data || t("error.failed.to.load.visibility"));
        }
    }
);

// Create the slice
const linksSlice = createSlice({
    name: 'links',
    initialState: {
        data: [],
        loading: false,
        error: null,
        pagination: {
            current: 1,
            pageSize: 8,
            total: 0,
        },
        sorter: { field: null, order: null },
        filters: {},
        visibility: [],
        tags: []
    },
    reducers: {
        setPagination: (state, action) => {
            state.pagination.current = action.payload.current;
            state.pagination.pageSize = action.payload.pageSize;
        },
        setSorter: (state, action) => {
            state.sorter = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLinks.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLinks.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.content;
                state.pagination = {
                    current: action.payload.number + 1, // Adjust for 0-based index
                    pageSize: action.payload.size,
                    total: action.payload.totalElements,
                };
            })
            .addCase(fetchLinks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchLinksByFilter.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.content;
                state.pagination = {
                    current: action.payload.number + 1, // Adjust for 0-based index
                    pageSize: action.payload.size,
                    total: action.payload.totalElements,
                };
            })
            .addCase(fetchTags.fulfilled, (state, action) => {
                state.loading = false;
                state.tags = action.payload;
            })
            .addCase(addLink.fulfilled, (state, action) => {
                state.data.push(action.payload);
            })
            .addCase(updateLink.fulfilled, (state, action) => {
                const index = state.data.findIndex(link => link.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
            })
            .addCase(deleteLink.fulfilled, (state, action) => {
                state.data = state.data.filter(link => link.id !== action.payload);
            })
            .addCase(fetchLinksVisibility.fulfilled, (state, action) => {
                state.visibility = action.payload;
            });
    },
});

export const { setPagination, setSorter, setFilters } = linksSlice.actions;
export default linksSlice.reducer;